import type * as Y from 'yjs'
import {
  BOARD_TEMPLATE_GAP,
  DEFAULT_FRAME_STYLE,
  boardTemplateBounds,
  type BoardTemplateContent,
  type Frame,
} from '@praesto/shared'
import { addFrame, createFrameId, framesOf, nextFrameOrder } from './frames.js'

/**
 * Ein Gerüst auf dem Board absetzen.
 *
 * Anders als bei der Frame-Vorlage gibt es hier nichts zu kopieren: Es entstehen
 * leere Frames, sonst nichts. Alles in einer Transaktion — sieben Frames, die
 * einzeln erscheinen, wären für Mitarbeitende am selben Board ein Flackern und
 * für „Rückgängig" sieben Schritte statt einem.
 *
 * Zurück kommen die angelegten Frames mit ihren Notizen: Die liegen nicht im
 * Dokument, sondern in Postgres, und müssen vom Aufrufer nachgereicht werden —
 * er allein weiß, ob er dafür überhaupt das Recht hat.
 */
export function insertBoardTemplate(
  doc: Y.Doc,
  content: BoardTemplateContent,
  at: { x: number; y: number },
): { id: string; notes: string }[] {
  const created: { id: string; notes: string }[] = []
  const baseOrder = nextFrameOrder(doc)

  doc.transact(() => {
    content.frames.forEach((entry, index) => {
      const frame: Frame = {
        id: createFrameId(),
        name: entry.name || `Frame ${index + 1}`,
        x: at.x + entry.x,
        y: at.y + entry.y,
        w: entry.w,
        h: entry.h,
        order: baseOrder + index,
        ...(entry.style ?? DEFAULT_FRAME_STYLE),
      }
      addFrame(doc, frame)
      created.push({ id: frame.id, notes: entry.notes })
    })
  })

  return created
}

/**
 * Wo das Gerüst hin soll.
 *
 * Auf einem leeren Board in den Ursprung — dorthin schaut die Kamera beim
 * Öffnen. Auf einem Board mit Inhalt rechts daneben: Ein Gerüst, das sich über
 * Vorhandenes legt, wäre nicht zu entwirren, und rechts ist die Richtung, in
 * der ein Vortrag ohnehin weiterläuft.
 */
export function boardTemplateOrigin(
  doc: Y.Doc,
  content: BoardTemplateContent,
): { x: number; y: number } {
  const frames = framesOf(doc)
  if (frames.length === 0) return { x: 0, y: 0 }

  const right = Math.max(...frames.map((frame) => frame.x + frame.w))
  const top = Math.min(...frames.map((frame) => frame.y))
  // `bounds` nur der Vollständigkeit halber ausgelesen: Der Inhalt beginnt zwar
  // gespeichert bei (0,0), aber darauf zu bauen hieße, es hier stillschweigend
  // vorauszusetzen.
  const bounds = boardTemplateBounds(content)
  return { x: right + BOARD_TEMPLATE_GAP - bounds.x, y: top - bounds.y }
}
